import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const ScenarioCtx = createContext(null);
const ACTIVE_STORAGE_KEY = 'indielaw_active_scenario';
// sessionStorage: UI preference that auto-clears with the tab (consistent with auth).
const storage = typeof window !== 'undefined' ? window.sessionStorage : null;

export function ScenarioProvider({ children }) {
  const { user } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [activeId, setActiveIdState] = useState(() => storage?.getItem(ACTIVE_STORAGE_KEY) || null);
  const [forecastData, setForecastData] = useState(null);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [error, setError] = useState(null);

  const setActive = useCallback((id) => {
    setActiveIdState(id);
    if (id) storage?.setItem(ACTIVE_STORAGE_KEY, id);
  }, []);

  const loadScenarios = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/scenarios');
      setScenarios(data);
      const base = data.find((s) => s.keystone) || data.find((s) => s.tone === 'base') || data[0];
      // Phase 2 (Feb 2026) — scenario picker retired. Always land on the
      // Keystone regardless of any previous localStorage selection, so
      // every surface reads the single active plan.
      setActiveIdState(() => {
        if (base) {
          storage?.setItem(ACTIVE_STORAGE_KEY, base.id);
          return base.id;
        }
        return null;
      });
    } catch (e) {
      setError(e?.message || 'Could not load scenarios');
    }
  }, [user]);

  const loadForecast = useCallback(async (id) => {
    if (!id) return;
    setLoadingForecast(true);
    try {
      const { data } = await api.get(`/scenarios/${id}/forecast`);
      setForecastData(data);
    } catch (e) {
      setError(e?.message || 'Could not load forecast');
    } finally {
      setLoadingForecast(false);
    }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);
  useEffect(() => { if (activeId) loadForecast(activeId); }, [activeId, loadForecast]);

  const updateAssumptions = useCallback(async (id, patch) => {
    const { data } = await api.patch(`/scenarios/${id}`, { assumptions: patch });
    setScenarios((prev) => prev.map((s) => (s.id === id ? data : s)));
    if (id === activeId) await loadForecast(id);
    return data;
  }, [activeId, loadForecast]);

  const createScenario = useCallback(async (payload) => {
    const { data } = await api.post('/scenarios', payload);
    setScenarios((prev) => [...prev, data]);
    return data;
  }, []);

  const patchScenario = useCallback(async (id, payload) => {
    const { data } = await api.patch(`/scenarios/${id}`, payload);
    setScenarios((prev) => prev.map((s) => (s.id === id ? data : s)));
    return data;
  }, []);

  const deleteScenario = useCallback(async (id) => {
    await api.delete(`/scenarios/${id}`);
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const active = useMemo(() => scenarios.find((s) => s.id === activeId) || null, [scenarios, activeId]);

  const reloadForecast = useCallback(() => loadForecast(activeId), [loadForecast, activeId]);

  return (
    <ScenarioCtx.Provider value={{
      scenarios, active, activeId, setActive, forecastData, loadingForecast, error,
      updateAssumptions, createScenario, patchScenario, deleteScenario,
      reloadScenarios: loadScenarios, reloadForecast,
    }}>
      {children}
    </ScenarioCtx.Provider>
  );
}

export const useScenario = () => useContext(ScenarioCtx);
